import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { isAdminUser } from '../lib/userRoles';
import {
  MAX_USER_PRICE_REDUCTION_EUR,
  clampExportPriceAdjustmentEur,
  parseExportPriceAdjustmentAmount,
  getDrawingPriceAdjustmentState,
  getDrawingPriceAdjustmentExVat,
} from '../lib/pricingAdjustments';

/**
 * Per-drawing add/subtract price adjustment (amount excl. BTW / subtotal),
 * shown under Additional items.
 */
const ConfiguratorDrawingPriceAdjustment = ({ config = {}, onConfigChange }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const state = getDrawingPriceAdjustmentState(config);
  const signedExVat = getDrawingPriceAdjustmentExVat(config);
  const [expanded, setExpanded] = useState(state.enabled);

  if (!onConfigChange) return null;

  const patch = (partial) => {
    Object.entries(partial).forEach(([key, value]) => onConfigChange(key, value));
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);

  return (
    <Card>
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="w-full flex items-start justify-between gap-3 text-left rounded-md -m-1 p-1 hover:bg-gray-50 transition-colors"
          aria-expanded={expanded}
        >
          <div className="min-w-0">
            <CardTitle className="text-base">{t('options.drawingPriceAdjustment')}</CardTitle>
            <CardDescription className="text-xs mt-1">
              {expanded
                ? t('options.drawingPriceAdjustmentDesc')
                : state.enabled && signedExVat !== 0
                  ? t('options.drawingPriceAdjustmentCollapsedActive', {
                      sign: signedExVat > 0 ? '+' : '−',
                      amount: formatPrice(Math.abs(signedExVat)),
                    })
                  : t('options.drawingPriceAdjustmentCollapsedHint')}
            </CardDescription>
          </div>
          <span className="shrink-0 mt-0.5 text-gray-500" aria-hidden>
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </span>
        </button>
      </CardHeader>

      {expanded ? (
        <CardContent className="space-y-3 pt-0">
          <label
            htmlFor="drawing-price-adj-enabled"
            className="flex items-center gap-2 cursor-pointer"
          >
            <Checkbox
              id="drawing-price-adj-enabled"
              checked={state.enabled}
              onCheckedChange={(checked) => {
                const enabled = checked === true;
                const mode = config.drawingPriceAdjustmentMode === 'add' ? 'add' : 'subtract';
                patch({
                  drawingPriceAdjustmentEnabled: enabled,
                  drawingPriceAdjustmentMode: mode,
                  drawingPriceAdjustmentAmount: enabled
                    ? clampExportPriceAdjustmentEur(
                        config.drawingPriceAdjustmentAmount,
                        mode,
                        { isAdmin }
                      )
                    : 0,
                });
              }}
              className="h-4 w-4"
            />
            <span className="text-sm text-gray-700">{t('quote.priceAdjustmentApply')}</span>
          </label>

          {state.enabled ? (
            <div className="flex flex-col gap-2 pl-6">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    patch({
                      drawingPriceAdjustmentMode: 'add',
                      drawingPriceAdjustmentAmount: clampExportPriceAdjustmentEur(
                        config.drawingPriceAdjustmentAmount,
                        'add',
                        { isAdmin }
                      ),
                    })
                  }
                  className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors ${
                    state.mode === 'add'
                      ? 'border-green-600 bg-green-50 text-green-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t('quote.priceAdjustmentAdd')}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    patch({
                      drawingPriceAdjustmentMode: 'subtract',
                      drawingPriceAdjustmentAmount: clampExportPriceAdjustmentEur(
                        config.drawingPriceAdjustmentAmount,
                        'subtract',
                        { isAdmin }
                      ),
                    })
                  }
                  className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors ${
                    state.mode === 'subtract'
                      ? 'border-orange-600 bg-orange-50 text-orange-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t('quote.priceAdjustmentSubtract')}
                </button>
              </div>

              <div className="relative flex-1 min-w-0">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  €
                </span>
                <Input
                  id="drawing-price-adj-amount"
                  type="text"
                  inputMode="decimal"
                  value={
                    config.drawingPriceAdjustmentAmount === '' ||
                    config.drawingPriceAdjustmentAmount == null
                      ? ''
                      : String(config.drawingPriceAdjustmentAmount)
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patch({ drawingPriceAdjustmentAmount: '' });
                      return;
                    }
                    if (/^\d*[.,]?\d*$/.test(raw.trim())) {
                      patch({ drawingPriceAdjustmentAmount: raw });
                    }
                  }}
                  onBlur={() => {
                    patch({
                      drawingPriceAdjustmentAmount: clampExportPriceAdjustmentEur(
                        parseExportPriceAdjustmentAmount(config.drawingPriceAdjustmentAmount),
                        state.mode,
                        { isAdmin }
                      ),
                    });
                  }}
                  className="h-9 w-full pl-8 text-sm tabular-nums"
                  aria-label={t('options.drawingPriceAdjustmentAmount')}
                  placeholder="0"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-400">
                  {state.mode === 'subtract' && !isAdmin
                    ? t('quote.priceAdjustmentMaxReduce', { max: MAX_USER_PRICE_REDUCTION_EUR })
                    : state.mode === 'add'
                      ? t('quote.priceAdjustmentAddHint')
                      : t('quote.priceAdjustmentAdminHint')}
                </span>
                {signedExVat !== 0 ? (
                  <span
                    className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium tabular-nums ${
                      signedExVat > 0
                        ? 'border-green-200 bg-green-50 text-green-800'
                        : 'border-orange-200 bg-orange-50 text-orange-800'
                    }`}
                  >
                    {signedExVat > 0 ? '+' : '−'}
                    {formatPrice(Math.abs(signedExVat))}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
};

export default ConfiguratorDrawingPriceAdjustment;
